import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { isUnauthorizedError } from '@highlight-cp/auth-client/session-recheck';
import { showErrorNotification } from '@highlight-cp/core-ui';
import { useGetCurrentUser, useGetIsImpersonating } from '@highlight-cp/hooks';
import { setClientErrorUserId } from '@highlight-cp/metrics/client';
import { userSliceActions, userSliceSelectors } from '@highlight-cp/redux-state';
import {
  resetAmplitudeUser,
  setAmplitudeUser,
  setImpersonationContext,
  setOrganizationProperties,
  trackLogin,
} from '@highlight-cp/user-analytics';
import { useAppDispatch } from 'app/hooks/redux';
import type { ClientPortalImpersonator } from '../../auth/appSession';
import { useClientPortalAuth } from '../../auth/ClientPortalAuthProvider';
import { ClientErrorReportingProvider } from 'app/components/client-error-reporting';
import { maybeIdentifyHubspotVisitor } from 'features/hubspot/widget';

type CurrentUserResult = ReturnType<typeof useGetCurrentUser>;
type SessionUserContextValue = {
  user: CurrentUserResult['data'] | null;
  teams: NonNullable<CurrentUserResult['data']>['teams'] | [];
  /**
   * The Better Auth impersonation target — also the identity customer lookups
   * key off, so it stays empty for admin-scoped sessions, which have none.
   * Ask `useGetIsImpersonating` whether an employee is behind the screen.
   */
  impersonating?: CurrentUserResult['impersonating'];
  impersonator?: ClientPortalImpersonator;
  isLoading?: boolean;
};

const emptySessionUser: SessionUserContextValue = { user: null, teams: [] };
const SessionUserContext = React.createContext<SessionUserContextValue | null>(null);

export function ClientPortalUserProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const auth = useClientPortalAuth();
  const session = auth.status === 'authenticated' ? auth.session : undefined;
  const userId = useSelector(userSliceSelectors.id);
  const sessionUserId = Number(session?.user_id);
  const hasValidSessionUserId = Number.isSafeInteger(sessionUserId) && sessionUserId > 0;
  const hasMatchingUserId = hasValidSessionUserId && userId === sessionUserId;
  const shouldSkipCurrentUser = auth.status !== 'authenticated' || !hasMatchingUserId;
  const {
    data: currentUser,
    error: currentUserError,
    isLoading: isCurrentUserLoading,
  } = useGetCurrentUser({ skip: shouldSkipCurrentUser });

  // Both entry paths, so the banner and Amplitude agree with the App Router.
  const isImpersonating = useGetIsImpersonating();
  const impersonator = session?.impersonator;
  const hasCurrentUserError = !shouldSkipCurrentUser && Boolean(currentUserError);
  // A 401 means the session ended, not a system error: the session recheck sends the tab to login.
  const hasSystemError = hasCurrentUserError && !isUnauthorizedError(currentUserError);
  const emailAddress = currentUser?.emailAddress ?? '';
  const customerEmail = session?.impersonation_target?.email ?? emailAddress;

  useEffect(() => {
    if (auth.status === 'loading') {
      return;
    }

    if (auth.status !== 'authenticated' || !hasValidSessionUserId) {
      if (userId !== null) {
        dispatch(userSliceActions.clearUserId());
      }
      return;
    }

    if (userId !== sessionUserId) {
      dispatch(userSliceActions.setUserId(sessionUserId));
    }
  }, [auth.status, dispatch, hasValidSessionUserId, sessionUserId, userId]);

  useEffect(() => {
    // When an impersonation session ends (was impersonating, now not), clear the
    // Amplitude identity so the acting employee's device/session doesn't carry into
    // the next customer/admin session on the same browser. This runs BEFORE the
    // identify effect below so that, on an in-place impersonation-end transition, the
    // reset happens first and the identify effect then re-identifies the current user
    // on a fresh device_id (rather than the reset clobbering a just-set identity).
    //
    // The "was impersonating" signal is persisted in sessionStorage (not a per-mount
    // ref) so the true→false edge is still detected when the exit comes back as a
    // remount/reload of this provider within the same tab.
    if (typeof window === 'undefined') {
      return;
    }

    const wasImpersonating = window.sessionStorage.getItem('hl_amp_was_impersonating') === 'true';

    if (wasImpersonating && !isImpersonating) {
      resetAmplitudeUser();
    }

    if (isImpersonating) {
      window.sessionStorage.setItem('hl_amp_was_impersonating', 'true');
    } else {
      window.sessionStorage.removeItem('hl_amp_was_impersonating');
    }
  }, [isImpersonating]);

  useEffect(() => {
    if (!session || !currentUser || !userId || !hasMatchingUserId) {
      return;
    }

    const userRole = isImpersonating ? 'impersonator' : 'customer';
    const userOrg = currentUser?.organization?.id?.toString() ?? '';

    setAmplitudeUser({
      userId: userId.toString(),
      emailAddress: customerEmail,
      orgId: userOrg,
      orgName: currentUser?.organization?.name ?? '',
      userRole,
      firstName: currentUser?.firstName ?? undefined,
      lastName: currentUser?.lastName ?? undefined,
    });

    trackLogin({ login_method: 'credentials' });

    if (userOrg && currentUser?.organization) {
      setOrganizationProperties({
        orgId: userOrg,
        orgName: currentUser.organization.name ?? '',
        teamCount: currentUser.organization.teams?.length ?? 0,
      });
    }

    if (impersonator) {
      // Tag with the impersonating employee's Heimdall id (NOT the customer's user_id),
      // so impersonated events are filterable by who acted. Email is deferred.
      setImpersonationContext({ id: impersonator.id });
    } else {
      setImpersonationContext(null);
    }

    void maybeIdentifyHubspotVisitor({
      email: customerEmail,
      userId: userId.toString(),
    });
  }, [
    currentUser,
    customerEmail,
    hasMatchingUserId,
    impersonator,
    isImpersonating,
    session,
    userId,
  ]);

  useEffect(() => {
    if (!hasSystemError) {
      return;
    }

    // Mantine skips a notification whose id is already on screen.
    showErrorNotification({
      id: 'current-user-error',
      message:
        "Sorry! We've encountered a system error, please contact us if the problem persists.",
    });
  }, [hasSystemError]);

  useEffect(() => {
    const id =
      auth.status === 'authenticated' && currentUser?.id != null ? String(currentUser.id) : null;
    setClientErrorUserId(id);

    return () => {
      setClientErrorUserId(null);
    };
  }, [auth.status, currentUser?.id]);

  const value = useMemo<SessionUserContextValue>(() => {
    if (hasCurrentUserError || !session || !currentUser) {
      return emptySessionUser;
    }

    return {
      user: currentUser,
      teams: currentUser?.teams ?? [],
      impersonating: session?.impersonation_target,
      impersonator,
      isLoading: isCurrentUserLoading,
    };
  }, [currentUser, hasCurrentUserError, impersonator, isCurrentUserLoading, session]);

  return (
    <SessionUserContext.Provider value={value}>
      <ClientErrorReportingProvider>{children}</ClientErrorReportingProvider>
    </SessionUserContext.Provider>
  );
}

export const useSessionUser = () => {
  const value = React.useContext(SessionUserContext);

  if (!value) {
    throw new Error('ClientPortalUserProvider is missing');
  }

  return value;
};
